import { getCompany } from "features/company/companyActions";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { artManagerVersion, isArtManager, openFolder } from "utils/artManager";

/**
 * Art Manager → Test.
 *
 * The first page meant to be used INSIDE the ERP Artwork Manager desktop app
 * (docs/erp-artwork-manager.md). It shows the company's Artwork Location
 * from Settings and offers to open that folder. The open itself is done by
 * the desktop app -- a web page cannot open a folder on the shop server --
 * so in a plain browser the button renders disabled and says why, which is
 * the whole point of this page: to make the app/browser difference visible
 * before anything real is built on it.
 */
export default function ArtManagerTest() {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company?.company);
  const location = (company?.artworkLocation || "").trim();
  const inApp = isArtManager();

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, message }

  // The setting can change in another tab or in the app; take it fresh
  // rather than trusting whatever the slice loaded at login.
  useEffect(() => {
    dispatch(getCompany());
  }, [dispatch]);

  const open = async () => {
    setBusy(true);
    setResult(null);
    const r = await openFolder(location);
    setResult(r);
    setBusy(false);
  };

  const disabledReason = !inApp
    ? "Open this page in the ERP Artwork Manager app to use it"
    : !location
      ? "Set the Artwork Location in Settings first"
      : undefined;

  return (
    <div className="px-3 py-3" style={{ marginLeft: 260, minHeight: "100vh" }}>
      <div className="d-flex align-items-center gap-2 mb-3">
        <h3 className="mb-0">Art Manager — Test</h3>
        {inApp ? (
          <span className="badge bg-success">
            Running in ERP Artwork Manager v{artManagerVersion()}
          </span>
        ) : (
          <span className="badge bg-secondary">Open in a browser</span>
        )}
      </div>

      <div className="card border mb-3" style={{ maxWidth: 720 }}>
        <div className="card-body">
          <div className="fw-bold mb-1">Artwork Location</div>
          {location ? (
            <code className="d-block mb-3" style={{ fontSize: 15 }}>
              {location}
            </code>
          ) : (
            <div className="text-muted mb-3">
              Not set. <Link to="/settings">Set it in Settings</Link> under
              Company Info → Artwork Manager.
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary"
            onClick={open}
            disabled={busy || !!disabledReason}
            title={disabledReason}
          >
            <i className="mdi mdi-folder-open me-1" aria-hidden="true" />
            {busy ? "Opening…" : "Open Folder"}
          </button>

          {disabledReason && (
            <div className="form-text mt-2">{disabledReason}.</div>
          )}

          {result && (
            <div
              className={`alert py-2 px-3 mt-3 mb-0 ${
                result.ok ? "alert-success" : "alert-danger"
              }`}
            >
              {result.ok ? "Folder opened." : result.message}
            </div>
          )}
        </div>
      </div>

      <p className="text-muted small" style={{ maxWidth: 720 }}>
        This page is the same website the app shows; only the button above
        needs the app. Nothing here writes anything.
      </p>
    </div>
  );
}
